import React, { useState, useEffect } from 'react';
import { sans, mono, coral, coralSoft, useTheme } from '../../tokens';
import { FileText, Table2, Presentation, Download, Plus, Trash2, Pencil, Check, X } from 'lucide-react';
import {
  getDocVault, saveDocVault, subscribeToDocVaultChanges, newDocId,
} from '../../data/documentsStore';

const FILE_TYPE_STYLES = {
  PDF:  { bg: '#FCEBEB', color: '#C0392B' },
  XLSX: { bg: '#E1F5EE', color: '#1D9E75' },
  PPTX: { bg: '#FFF3E0', color: '#BA7517' },
  DOCX: { bg: '#E3F2FD', color: '#1976D2' },
  CSV:  { bg: '#E1F5EE', color: '#1D9E75' },
  LINK: { bg: '#E3E6F3', color: '#4A5578' },
};

function getFileIcon(type) {
  const size = 14;
  switch (type) {
    case 'PDF':  return <FileText size={size} style={{ color: '#C0392B' }} />;
    case 'XLSX': return <Table2 size={size} style={{ color: '#1D9E75' }} />;
    case 'CSV':  return <Table2 size={size} style={{ color: '#1D9E75' }} />;
    case 'PPTX': return <Presentation size={size} style={{ color: '#BA7517' }} />;
    case 'DOCX': return <FileText size={size} style={{ color: '#1976D2' }} />;
    default:     return <FileText size={size} style={{ color: '#888' }} />;
  }
}

function formatDocDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
}

const FILE_TYPES = ['PDF', 'XLSX', 'PPTX', 'DOCX', 'CSV', 'LINK'];

export default function DocumentVault({ companyId, readOnly = false }) {
  const t = useTheme();
  const [vault, setVault] = useState(() => getDocVault(companyId));
  const [activeCategory, setActiveCategory] = useState(vault.categories[0] || 'Finance');
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [addingDoc, setAddingDoc] = useState(false);
  const [docDraft, setDocDraft] = useState({ name: '', type: 'PDF', url: '', date: new Date().toISOString().split('T')[0] });
  const [editingDocId, setEditingDocId] = useState(null);

  useEffect(() => subscribeToDocVaultChanges(() => setVault(getDocVault(companyId))), [companyId]);
  useEffect(() => {
    // Keep activeCategory valid if categories change
    if (!vault.categories.includes(activeCategory) && vault.categories.length > 0) {
      setActiveCategory(vault.categories[0]);
    }
  }, [vault.categories, activeCategory]);

  const persist = (next) => { setVault(next); saveDocVault(companyId, next); };

  const addCategory = () => {
    const name = newCategoryName.trim();
    if (!name || vault.categories.includes(name)) { setNewCategoryName(''); setAddingCategory(false); return; }
    persist({ ...vault, categories: [...vault.categories, name] });
    setActiveCategory(name);
    setNewCategoryName('');
    setAddingCategory(false);
  };
  const removeCategory = (cat) => {
    if (!window.confirm(`Delete "${cat}" vault and all its documents?`)) return;
    const nextCats = vault.categories.filter(c => c !== cat);
    const nextDocs = vault.documents.filter(d => d.category !== cat);
    persist({ categories: nextCats, documents: nextDocs });
  };

  const openDocForm = () => {
    setAddingDoc(true);
    setEditingDocId(null);
    setDocDraft({ name: '', type: 'PDF', url: '', date: new Date().toISOString().split('T')[0] });
  };
  const cancelDocForm = () => { setAddingDoc(false); setEditingDocId(null); };
  const saveDocForm = () => {
    if (!docDraft.name.trim()) return;
    if (editingDocId) {
      persist({ ...vault, documents: vault.documents.map(d => d.id === editingDocId ? { ...d, ...docDraft } : d) });
    } else {
      const doc = { id: newDocId(), category: activeCategory, ...docDraft };
      persist({ ...vault, documents: [doc, ...vault.documents] });
    }
    cancelDocForm();
  };
  const startEditDoc = (doc) => {
    setAddingDoc(true);
    setEditingDocId(doc.id);
    setDocDraft({ name: doc.name || '', type: doc.type || 'PDF', url: doc.url || '', date: doc.date || '' });
  };
  const removeDoc = (id) => {
    persist({ ...vault, documents: vault.documents.filter(d => d.id !== id) });
  };

  const filtered = vault.documents.filter(d => d.category === activeCategory);

  const inputStyle = {
    padding: '5px 10px', borderRadius: 4, border: `1px solid ${t.border}`,
    background: t.bgCard, color: t.textPrimary, fontFamily: sans, fontSize: 11, outline: 'none',
  };

  return (
    <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 10, padding: '20px 24px' }}>
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: t.textMuted, fontFamily: sans }}>
          Document Vault
        </div>
        <div style={{ fontSize: 10, color: t.textFaint, fontFamily: sans, marginTop: 2 }}>
          Investor data room
        </div>
      </div>

      {/* Category tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        {vault.categories.map(cat => {
          const active = activeCategory === cat;
          return (
            <div key={cat} style={{ display: 'flex', alignItems: 'center' }}>
              <button
                onClick={() => setActiveCategory(cat)}
                style={{
                  padding: '4px 14px',
                  borderRadius: 12,
                  border: `1px solid ${active ? coral : t.border}`,
                  background: active ? coralSoft : 'transparent',
                  color: active ? coral : t.textMuted,
                  fontSize: 10, fontWeight: 600, fontFamily: sans, cursor: 'pointer',
                }}
              >{cat}</button>
              {!readOnly && active && (
                <button onClick={() => removeCategory(cat)} title={`Delete "${cat}" vault`}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#C0392B', padding: '2px 4px', marginLeft: 2 }}>
                  <Trash2 size={11} />
                </button>
              )}
            </div>
          );
        })}
        {!readOnly && (addingCategory ? (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <input
              value={newCategoryName}
              onChange={e => setNewCategoryName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addCategory(); if (e.key === 'Escape') { setAddingCategory(false); setNewCategoryName(''); } }}
              placeholder="New vault name"
              autoFocus
              style={{ ...inputStyle, width: 140, fontSize: 10, padding: '3px 8px' }}
            />
            <button onClick={addCategory} style={{ background: coral, border: 'none', borderRadius: 4, color: '#fff', padding: 4, cursor: 'pointer' }}>
              <Check size={12} />
            </button>
            <button onClick={() => { setAddingCategory(false); setNewCategoryName(''); }}
              style={{ background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 4, color: t.textMuted, padding: 4, cursor: 'pointer' }}>
              <X size={12} />
            </button>
          </div>
        ) : (
          <button onClick={() => setAddingCategory(true)} title="Add vault"
            style={{
              padding: '4px 10px', borderRadius: 12,
              border: `1px dashed ${t.border}`, background: 'transparent',
              color: t.textMuted, fontSize: 10, fontWeight: 600, fontFamily: sans, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
            <Plus size={10} /> Add vault
          </button>
        ))}
      </div>

      {/* Add / edit document form */}
      {!readOnly && addingDoc && (
        <div style={{
          padding: 12, marginBottom: 12, borderRadius: 8,
          background: t.bgSurface, border: `1px solid ${t.border}`,
          display: 'grid', gap: 8,
        }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={docDraft.name}
              onChange={e => setDocDraft({ ...docDraft, name: e.target.value })}
              placeholder="Document name"
              style={{ ...inputStyle, flex: 1 }}
            />
            <select
              value={docDraft.type}
              onChange={e => setDocDraft({ ...docDraft, type: e.target.value })}
              style={{ ...inputStyle, width: 90 }}
            >
              {FILE_TYPES.map(tp => <option key={tp} value={tp}>{tp}</option>)}
            </select>
            <input
              type="date"
              value={docDraft.date}
              onChange={e => setDocDraft({ ...docDraft, date: e.target.value })}
              style={{ ...inputStyle, width: 140 }}
            />
          </div>
          <input
            value={docDraft.url}
            onChange={e => setDocDraft({ ...docDraft, url: e.target.value })}
            placeholder="URL (Google Drive, Dropbox, Supabase Storage...) — optional"
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={saveDocForm} disabled={!docDraft.name.trim()} style={{
              display: 'flex', alignItems: 'center', gap: 4,
              background: docDraft.name.trim() ? coral : t.borderLight,
              border: 'none', borderRadius: 4, padding: '4px 10px',
              color: docDraft.name.trim() ? '#fff' : t.textMuted,
              fontFamily: sans, fontSize: 10, fontWeight: 600, cursor: docDraft.name.trim() ? 'pointer' : 'not-allowed',
            }}>
              <Check size={11} /> {editingDocId ? 'Save' : 'Add'}
            </button>
            <button onClick={cancelDocForm} style={{
              display: 'flex', alignItems: 'center', gap: 4,
              background: 'none', border: `1px solid ${t.border}`, borderRadius: 4,
              padding: '4px 10px', color: t.textSecondary,
              fontFamily: sans, fontSize: 10, cursor: 'pointer',
            }}>
              <X size={11} /> Cancel
            </button>
          </div>
        </div>
      )}

      {!readOnly && !addingDoc && (
        <button onClick={openDocForm} style={{
          marginBottom: 10, padding: '5px 12px', borderRadius: 6,
          border: `1px dashed ${t.border}`, background: 'transparent',
          color: t.textMuted, fontFamily: sans, fontSize: 10, fontWeight: 600,
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <Plus size={11} /> Add document to {activeCategory}
        </button>
      )}

      {/* Document list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {filtered.length === 0 && (
          <div style={{
            padding: '32px 16px', textAlign: 'center',
            border: `1px dashed ${t.borderLight}`, borderRadius: 8,
          }}>
            <div style={{ fontSize: 11, color: t.textMuted, fontFamily: sans, fontWeight: 600, marginBottom: 4 }}>
              No documents uploaded yet
            </div>
            <div style={{ fontSize: 10, color: t.textFaint, fontFamily: sans }}>
              {activeCategory} documents will appear here once added.
            </div>
          </div>
        )}
        {filtered.map((doc, i) => {
          const typeStyle = FILE_TYPE_STYLES[doc.type] || { bg: t.pillBg, color: t.textMuted };
          const canDownload = !!doc.url;
          return (
            <div key={doc.id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 0',
              borderBottom: i < filtered.length - 1 ? `1px solid ${t.borderLight}` : 'none',
            }}>
              <span style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 28, height: 28, borderRadius: 6,
                background: typeStyle.bg, flexShrink: 0,
              }}>
                {getFileIcon(doc.type)}
              </span>
              <span style={{
                flex: 1, fontSize: 11, fontFamily: sans, color: t.textPrimary,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {doc.name}
              </span>
              <span style={{
                fontSize: 10, fontFamily: mono, color: t.textFaint,
                flexShrink: 0, minWidth: 80,
              }}>
                {formatDocDate(doc.date)}
              </span>
              <button
                disabled={!canDownload}
                onClick={() => { if (canDownload) window.open(doc.url, '_blank', 'noopener,noreferrer'); }}
                title={canDownload ? 'Open' : 'No URL attached'}
                style={{
                  background: t.bgSurface, border: `1px solid ${t.borderLight}`,
                  borderRadius: 4, padding: '3px 8px',
                  fontSize: 9, fontFamily: sans, color: t.textMuted,
                  cursor: canDownload ? 'pointer' : 'not-allowed',
                  opacity: canDownload ? 1 : 0.45,
                  flexShrink: 0,
                  display: 'flex', alignItems: 'center', gap: 4,
                }}>
                <Download size={10} />
                {doc.type === 'LINK' ? 'Open' : 'Download'}
              </button>
              {!readOnly && (
                <div style={{ display: 'flex', gap: 2 }}>
                  <button onClick={() => startEditDoc(doc)} title="Edit"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: t.textMuted, padding: 4 }}>
                    <Pencil size={12} />
                  </button>
                  <button onClick={() => removeDoc(doc.id)} title="Delete"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#C0392B', padding: 4 }}>
                    <Trash2 size={12} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
